import type {TFunction} from 'i18next'
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import type {Page} from '@/hooks/empresa/organizacion/use-businesses'
import {mintUploadTarget, uploadToStorage} from '@/hooks/sgsst/evidencias/use-evidence'
import type {components} from '@/lib/api-schema'

export type Matriz = components['schemas']['MatrizRead']
export type Fila = components['schemas']['FilaRead']
export type RiesgoFilaNd = components['schemas']['RiesgoFilaNd']
export type RiesgoFilaClasificacion = components['schemas']['RiesgoFilaClasificacion']
export type RiesgoFilaInterpretacion = components['schemas']['RiesgoFilaInterpretacion']
export type RiesgoMatrizMetodologia = components['schemas']['RiesgoMatrizMetodologia']
export type FilaOrigenRead = components['schemas']['FilaOrigenRead']
export type RiesgoFilaControlTipo = components['schemas']['RiesgoFilaControlTipo']
export type SgsstActionJerarquia = components['schemas']['SgsstActionJerarquia']
export type ControlCatalogEntry = components['schemas']['ControlCatalogRead']
export type ControlFoto = components['schemas']['ControlFotoRead']
export type MejoraEditada = components['schemas']['MejoraConfigurable']
export type NormaLigada = components['schemas']['NormaLigadaRead']

// Orden legal de la jerarquía de controles (Decreto 1072 art. 2.2.4.6.24): eliminación primero,
// EPP último; nunca se reordena para mostrar.
export const JERARQUIAS: SgsstActionJerarquia[] = ['eliminacion', 'sustitucion', 'ingenieria', 'administrativo', 'epp']

export const CONTROL_TIPOS: RiesgoFilaControlTipo[] = ['fuente', 'medio', 'individuo']

// Las 7 familias del Anexo A de la GTC 45, en el orden fijo que comparte el módulo.
export const CLASIFICACIONES: RiesgoFilaClasificacion[] = [
  'biologico',
  'fisico',
  'quimico',
  'biomecanico',
  'psicosocial',
  'condiciones_de_seguridad',
  'fenomenos_naturales',
]

// La columna del backend sigue siendo texto libre por filas antiguas, pero la UI solo ofrece
// estos dos valores, en mayúsculas como los espera la exportación GTC 45.
export const PROCESOS = ['ADMINISTRATIVO', 'OPERATIVO'] as const

// Solo mapea la opción al enum de la API; la aritmética GTC 45 vive en el backend.
export const ND_BY_VALOR: Record<string, RiesgoFilaNd> = {
  '10': 'muy_alto_10',
  '6': 'alto_6',
  '2': 'medio_2',
  bajo: 'bajo',
}

export function ndToValorKey(nd: RiesgoFilaNd | null | undefined): string {
  if (nd === 'muy_alto_10') return '10'
  if (nd === 'alto_6') return '6'
  if (nd === 'medio_2') return '2'
  return 'bajo'
}

export const INTERPRETACION_TONE: Record<RiesgoFilaInterpretacion, 'red' | 'orange' | 'amber' | 'lime'> = {
  I: 'red',
  II: 'orange',
  III: 'amber',
  IV: 'lime',
}

// Las familias son taxonomía, no magnitud: van en `neutral` (U2a, U2c). En la misma celda vive
// INTERPRETACION_TONE y dos rampas pegadas se leerían como una.
export const CLASIFICACION_TONE: Record<RiesgoFilaClasificacion, 'neutral'> = {
  biologico: 'neutral',
  quimico: 'neutral',
  fisico: 'neutral',
  condiciones_de_seguridad: 'neutral',
  biomecanico: 'neutral',
  psicosocial: 'neutral',
  fenomenos_naturales: 'neutral',
}

// Sondeo suave mientras corre una reconciliación fuera de banda («Consolidando aportes…»).
const RECONCILIACION_POLL_MS = 10_000

export type MapaCalor = components['schemas']['MapaCalorRead']

// Token reservado, nunca un id real: el backend junta ahí las áreas nulas o borradas.
export const SIN_AREA_TOKEN = 'sin_area'

export type MapaCalorFiltros = {
  incluirPropuestas: boolean
  proceso: string[]
  areaId: string[]
  clasificacion: RiesgoFilaClasificacion[]
}

export function useMapaCalor(businessId: string | undefined, filtros: MapaCalorFiltros, enabled: boolean) {
  return useQuery({
    queryKey: ['ipevr', businessId, 'mapa', filtros],
    queryFn: async () =>
      (
        await api.get<MapaCalor>(`/businesses/${businessId}/sgsst/matriz-riesgos/mapa`, {
          params: {
            incluir_propuestas: filtros.incluirPropuestas,
            proceso: filtros.proceso,
            area_id: filtros.areaId,
            clasificacion: filtros.clasificacion,
          },
          // axios pone corchetes en los arreglos (`proceso[]=a`) y `list[str] = Query(...)` de
          // FastAPI solo entiende la forma repetida (`proceso=a&proceso=b`).
          paramsSerializer: {indexes: null},
        })
      ).data,
    enabled: !!businessId && enabled,
  })
}

export function useMatrizRiesgos(businessId: string | undefined) {
  return useQuery({
    queryKey: ['ipevr', businessId],
    queryFn: async () => (await api.get<Matriz>(`/businesses/${businessId}/sgsst/matriz-riesgos`)).data,
    enabled: !!businessId,
    refetchInterval: (query) => (query.state.data?.reconciliacion_en_curso ? RECONCILIACION_POLL_MS : false),
  })
}

function useIpevrInvalidation(businessId: string) {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({queryKey: ['ipevr', businessId]})
    // 4.1.x sugeridos ride the shared worklist + checklist state.
    queryClient.invalidateQueries({queryKey: ['sgsst-pendientes', businessId]})
    queryClient.invalidateQueries({queryKey: ['sgsst', businessId]})
    queryClient.invalidateQueries({queryKey: ['actions', businessId]})
  }
}

export function useSetMetodologia(businessId: string) {
  const invalidate = useIpevrInvalidation(businessId)
  return useMutation({
    mutationFn: async (metodologia: RiesgoMatrizMetodologia) =>
      api.patch(`/businesses/${businessId}/sgsst/matriz-riesgos/metodologia`, {metodologia}),
    onSuccess: invalidate,
  })
}

export function useMarkRevision(businessId: string) {
  const invalidate = useIpevrInvalidation(businessId)
  return useMutation({
    mutationFn: async () => api.post(`/businesses/${businessId}/sgsst/matriz-riesgos/revision`),
    onSuccess: invalidate,
  })
}

export function useMarkVigente(businessId: string) {
  const invalidate = useIpevrInvalidation(businessId)
  return useMutation({
    mutationFn: async () => api.post(`/businesses/${businessId}/sgsst/matriz-riesgos/vigente`),
    onSuccess: invalidate,
  })
}

// `gate_situacion` solo es no nulo mientras `can_mark_vigente` es false.
const GATE_SITUACION_COPY_KEY: Record<NonNullable<Matriz['gate_situacion']>, string> = {
  responsable_acreditado: 'responsableAcreditado',
  responsable_categoria_insuficiente: 'categoriaInsuficiente',
  sin_responsable: 'sinResponsable',
}

export function gateBlockedCopy(t: TFunction, matriz: Matriz) {
  const key = GATE_SITUACION_COPY_KEY[matriz.gate_situacion ?? 'sin_responsable']
  const nombre = matriz.gate_responsable ? `${matriz.gate_responsable.name} ${matriz.gate_responsable.last_name}` : ''
  return {
    banner: t(`ipevr.gate.${key}.blocked`, {nombre}),
    tooltip: t(`ipevr.gate.${key}.blockedTooltip`, {nombre}),
    // T6/T8: el banner dice qué falta; el cómo va plegado en un `PorQue`.
    porque: t(`ipevr.gate.${key}.porque`, {nombre}),
  }
}

// 4.1.2 es el único código 4.x que aplica a los capítulos 1 y 2: cualquier empresa puede
// registrarlo.
const OTRA_METODOLOGIA_STANDARD_CODE = '4.1.2'

export type UploadProgress = (pct: number) => void

export function useUploadOtraEvidence(businessId: string) {
  const invalidate = useIpevrInvalidation(businessId)
  return useMutation({
    mutationFn: async ({file, onProgress}: {file: File; onProgress: UploadProgress}) => {
      const target = await mintUploadTarget(businessId, file)
      await uploadToStorage(target, file, onProgress)
      const registered = await api.post(`/businesses/${businessId}/sgsst/evidence`, {
        standard_code: OTRA_METODOLOGIA_STANDARD_CODE,
        object_key: target.object_key,
        filename: file.name,
        content_type: file.type,
        size_bytes: file.size,
        employee_ids: [],
      })
      const items = registered.data.items as {id: string; filename: string; uploaded_at: string}[]
      const match = items
        .filter((item) => item.filename === file.name)
        .sort((a, b) => (a.uploaded_at < b.uploaded_at ? 1 : -1))[0]
      if (!match) throw new Error('evidence registration did not return the uploaded file')
      return api.post(`/businesses/${businessId}/sgsst/matriz-riesgos/otra-evidence`, {evidence_id: match.id})
    },
    onSuccess: invalidate,
  })
}

// Solo lo que FilaCreate/FilaUpdate aceptan: controles y medidas viajan por sus propios endpoints.
export type FilaFields = {
  proceso?: string | null
  zona?: string | null
  actividad?: string | null
  tarea?: string | null
  rutinaria?: boolean
  clasificacion?: RiesgoFilaClasificacion
  descripcion?: string
  efectos_posibles?: string | null
  nd?: RiesgoFilaNd | null
  ne?: number | null
  nc?: number | null
  num_expuestos?: number | null
  area_id?: string | null
  peor_consecuencia?: string | null
  requisito_legal?: boolean
}

export function useCreateFila(businessId: string) {
  const invalidate = useIpevrInvalidation(businessId)
  return useMutation({
    mutationFn: async (input: FilaFields & {clasificacion: RiesgoFilaClasificacion; descripcion: string}) =>
      api.post(`/businesses/${businessId}/sgsst/matriz-riesgos/filas`, input),
    onSuccess: invalidate,
  })
}

export function useUpdateFila(businessId: string) {
  const invalidate = useIpevrInvalidation(businessId)
  return useMutation({
    mutationFn: async ({filaId, fields}: {filaId: string; fields: FilaFields}) =>
      api.patch(`/businesses/${businessId}/sgsst/matriz-riesgos/filas/${filaId}`, fields),
    onSuccess: invalidate,
  })
}

// Sacar un peligro de la matriz es criterio profesional: siempre pide motivo.
export function useDescartarFila(businessId: string) {
  const invalidate = useIpevrInvalidation(businessId)
  return useMutation({
    mutationFn: async ({filaId, motivo}: {filaId: string; motivo: string}) =>
      api.post(`/businesses/${businessId}/sgsst/matriz-riesgos/filas/${filaId}/descartar`, {motivo}),
    onSuccess: invalidate,
  })
}

// `conservarSinProcedencia` decide el caso de la ÚLTIMA procedencia; sin ella el backend
// responde 409 en vez de elegir por el profesional.
export function useDesasociarProcedencia(businessId: string) {
  const invalidate = useIpevrInvalidation(businessId)
  return useMutation({
    mutationFn: async ({
      filaId,
      hallazgoId,
      motivo,
      conservarSinProcedencia,
    }: {
      filaId: string
      hallazgoId: string
      motivo: string
      conservarSinProcedencia?: boolean
    }) =>
      api.post(`/businesses/${businessId}/sgsst/matriz-riesgos/filas/${filaId}/origen/${hallazgoId}/desasociar`, {
        motivo,
        conservar_sin_procedencia: conservarSinProcedencia ?? false,
      }),
    onSuccess: invalidate,
  })
}

export type Descarte = components['schemas']['DescarteRead']

export const DESCARTES_PAGE_SIZE = 20

export function useDescartes(businessId: string, enabled: boolean, page = 1) {
  return useQuery({
    queryKey: ['ipevr', businessId, 'descartes', page],
    queryFn: async () =>
      (
        await api.get<Page<Descarte>>(`/businesses/${businessId}/sgsst/matriz-riesgos/descartes`, {
          params: {page, page_size: DESCARTES_PAGE_SIZE},
        })
      ).data,
    enabled,
    placeholderData: (previous) => previous,
  })
}

export function useConfirmarFila(businessId: string) {
  const invalidate = useIpevrInvalidation(businessId)
  return useMutation({
    mutationFn: async (filaId: string) =>
      api.post(`/businesses/${businessId}/sgsst/matriz-riesgos/filas/${filaId}/confirmar`),
    onSuccess: invalidate,
  })
}

// Sin `jerarquia`/`titulo` crea la acción simple de la fila; con una medida sugerida es
// idempotente por (fila, jerarquia, titulo).
export function useCrearMejora(businessId: string) {
  const invalidate = useIpevrInvalidation(businessId)
  return useMutation({
    mutationFn: async ({
      filaId,
      jerarquia,
      titulo,
      editado,
    }: {
      filaId: string
      jerarquia?: SgsstActionJerarquia
      titulo?: string
      // Ausente, el endpoint deriva lo de siempre (descripción de la fila, rep. legal, +30 días).
      editado?: MejoraEditada
    }) =>
      api.post(`/businesses/${businessId}/sgsst/matriz-riesgos/filas/${filaId}/crear-mejora`, {
        jerarquia: jerarquia ?? null,
        titulo: titulo ?? null,
        ...(editado ?? {}),
      }),
    onSuccess: invalidate,
  })
}

/** Endpoint propio y no el GET del registro: ese trae la procedencia congelada y el cálculo de
 * novedades solo para llenar una lista de opciones. */
export function useNormasLigables(businessId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['normas-ligables', businessId],
    queryFn: async () =>
      (await api.get<NormaLigada[]>(`/businesses/${businessId}/sgsst/matriz-riesgos/normas-ligables`)).data,
    enabled: !!businessId && enabled,
  })
}

function useVinculoInvalidation(businessId: string) {
  const invalidate = useIpevrInvalidation(businessId)
  const queryClient = useQueryClient()
  return () => {
    invalidate()
    // El vínculo se ve desde los dos lados: el registro legal repinta sus chips.
    queryClient.invalidateQueries({queryKey: ['matriz-legal', businessId]})
  }
}

export function useLigarNorma(businessId: string) {
  const invalidate = useVinculoInvalidation(businessId)
  return useMutation({
    mutationFn: async ({filaId, legalItemId}: {filaId: string; legalItemId: string}) =>
      api.post(`/businesses/${businessId}/sgsst/matriz-riesgos/filas/${filaId}/normas`, {
        legal_item_id: legalItemId,
      }),
    onSuccess: invalidate,
  })
}

export function useDesligarNorma(businessId: string) {
  const invalidate = useVinculoInvalidation(businessId)
  return useMutation({
    mutationFn: async ({filaId, legalItemId}: {filaId: string; legalItemId: string}) =>
      api.delete(`/businesses/${businessId}/sgsst/matriz-riesgos/filas/${filaId}/normas/${legalItemId}`),
    onSuccess: invalidate,
  })
}

// Respaldo del dueño o responsable: sobre cualquier control vivo de la empresa, no solo los propios.
export function useUploadControlFoto(businessId: string) {
  const invalidate = useIpevrInvalidation(businessId)
  return useMutation({
    mutationFn: async ({controlId, file, onProgress}: {controlId: string; file: File; onProgress: UploadProgress}) => {
      const target = (
        await api.post<components['schemas']['ControlFotoUploadTargetRead']>(
          `/businesses/${businessId}/sgsst/matriz-riesgos/controles/${controlId}/foto-uploads`,
          {filename: file.name, content_type: file.type, size_bytes: file.size}
        )
      ).data
      await uploadToStorage(target, file, onProgress)
      return (
        await api.post<ControlFoto[]>(`/businesses/${businessId}/sgsst/matriz-riesgos/controles/${controlId}/fotos`, {
          object_key: target.object_key,
          filename: file.name,
          content_type: file.type,
          size_bytes: file.size,
        })
      ).data
    },
    onSuccess: invalidate,
  })
}

// Espejo de `MAX_FOTOS_POR_CONTROL` (backend/app/sgsst/riesgos/core/fotos_control.py).
export const MAX_FOTOS_POR_CONTROL = 10

export type ImportacionMatriz = components['schemas']['ImportacionRead']

const IMPORTACION_XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
// Espejo de `IMPORTACION_MAX_BYTES` (backend/app/sgsst/riesgos/schemas.py).
export const IMPORTACION_MAX_BYTES = 5 * 1024 * 1024

// El error lo pinta el diálogo, que lo muestra junto al archivo que falló.
export function useImportarMatriz(businessId: string) {
  const invalidate = useIpevrInvalidation(businessId)
  return useMutation({
    meta: {manejaError: true},
    mutationFn: async ({file, confirmar, onProgress}: {file: File; confirmar: boolean; onProgress: UploadProgress}) => {
      const base = `/businesses/${businessId}/sgsst/matriz-riesgos/importaciones`
      const target = (
        await api.post<components['schemas']['ImportacionUploadTargetRead']>(`${base}/destino`, {
          filename: file.name,
          content_type: IMPORTACION_XLSX,
          size_bytes: file.size,
        })
      ).data
      await uploadToStorage(target, file, onProgress, IMPORTACION_XLSX)
      return (
        await api.post<ImportacionMatriz>(base, {
          importacion_id: target.importacion_id,
          filename: file.name,
          size_bytes: file.size,
          confirmar,
        })
      ).data
    },
    onSuccess: invalidate,
  })
}

export function reporteDeImportacionUrl(businessId: string, importacionId: string) {
  return `/businesses/${businessId}/sgsst/matriz-riesgos/importaciones/${importacionId}/reporte.xlsx`
}

export function useDeleteControlFoto(businessId: string) {
  const invalidate = useIpevrInvalidation(businessId)
  return useMutation({
    mutationFn: async ({controlId, fotoId}: {controlId: string; fotoId: string}) =>
      api.delete(`/businesses/${businessId}/sgsst/matriz-riesgos/controles/${controlId}/fotos/${fotoId}`),
    onSuccess: invalidate,
  })
}

// Las fotos de control viajan en la lectura de la matriz: se invalida todo `ipevr`.
export function useControlFotoAnalysisAction(businessId: string) {
  const invalidate = useIpevrInvalidation(businessId)
  return useMutation({
    mutationFn: async ({
      controlId,
      fotoId,
      action,
    }: {
      controlId: string
      fotoId: string
      action: 'dismiss' | 'reanalyze'
    }) =>
      (
        await api.post<ControlFoto[]>(
          `/businesses/${businessId}/sgsst/matriz-riesgos/controles/${controlId}/fotos/${fotoId}/analysis/${action}`
        )
      ).data,
    onSuccess: invalidate,
  })
}

export function useFilaOrigen(businessId: string, filaId: string | undefined) {
  return useQuery({
    queryKey: ['ipevr', businessId, 'origen', filaId],
    queryFn: async () =>
      (await api.get<FilaOrigenRead>(`/businesses/${businessId}/sgsst/matriz-riesgos/filas/${filaId}/origen`)).data,
    enabled: !!filaId,
  })
}

export type NovedadesDiff = components['schemas']['NovedadesRead']

export function useFilaNovedades(businessId: string, filaId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['ipevr', businessId, 'novedades', filaId],
    queryFn: async () =>
      (await api.get<NovedadesDiff>(`/businesses/${businessId}/sgsst/matriz-riesgos/filas/${filaId}/novedades`)).data,
    enabled: !!filaId && enabled,
  })
}

export function useIncorporarNovedades(businessId: string) {
  const invalidate = useIpevrInvalidation(businessId)
  return useMutation({
    mutationFn: async (filaId: string) =>
      api.post(`/businesses/${businessId}/sgsst/matriz-riesgos/filas/${filaId}/novedades/incorporar`),
    onSuccess: invalidate,
  })
}

export function useMantenerFila(businessId: string) {
  const invalidate = useIpevrInvalidation(businessId)
  return useMutation({
    mutationFn: async (filaId: string) =>
      api.post(`/businesses/${businessId}/sgsst/matriz-riesgos/filas/${filaId}/novedades/mantener`),
    onSuccess: invalidate,
  })
}

export function useControlCatalog(
  businessId: string,
  clasificacion: RiesgoFilaClasificacion,
  tipo: RiesgoFilaControlTipo
) {
  return useQuery({
    queryKey: ['ipevr', businessId, 'control-catalog', clasificacion, tipo],
    queryFn: async () =>
      (
        await api.get<ControlCatalogEntry[]>(`/businesses/${businessId}/sgsst/matriz-riesgos/control-catalog`, {
          params: {clasificacion, tipo},
        })
      ).data,
  })
}

export function useControlHistory(businessId: string, tipo: RiesgoFilaControlTipo) {
  return useQuery({
    queryKey: ['ipevr', businessId, 'control-history', tipo],
    queryFn: async () =>
      (
        await api.get<string[]>(`/businesses/${businessId}/sgsst/matriz-riesgos/control-history`, {
          params: {tipo},
        })
      ).data,
  })
}

export type ControlCreate = {
  tipo: RiesgoFilaControlTipo
  texto: string
  catalog_key?: string | null
}

export function useAddFilaControl(businessId: string) {
  const invalidate = useIpevrInvalidation(businessId)
  return useMutation({
    mutationFn: async ({filaId, control}: {filaId: string; control: ControlCreate}) =>
      api.post(`/businesses/${businessId}/sgsst/matriz-riesgos/filas/${filaId}/controles`, control),
    onSuccess: invalidate,
  })
}

export function useRemoveFilaControl(businessId: string) {
  const invalidate = useIpevrInvalidation(businessId)
  return useMutation({
    mutationFn: async ({filaId, controlId}: {filaId: string; controlId: string}) =>
      api.delete(`/businesses/${businessId}/sgsst/matriz-riesgos/filas/${filaId}/controles/${controlId}`),
    onSuccess: invalidate,
  })
}
